import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Checkbox } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import api from '../services/api';
import AddressForm from './AddressForm';
import AddressList from './AddressList';
import AddressSearch from './AddressSearch';
import './AddEditUserModal.css';

const EMPTY_SHIPPING = { full_name: '', address_line1: '', address_line2: '', city: '', postal_code: '', country: 'Switzerland', is_default: false };
const EMPTY_BILLING  = { full_name: '', address_line1: '', address_line2: '', city: '', postal_code: '', country: 'Switzerland', company_name: '', vat_number: '', is_default: false };

const USER_EDIT_TABS = [
  { key: 'settings', label: 'Settings' },
  { key: 'shipping', label: 'Shipping Addresses' },
  { key: 'billing',  label: 'Billing Addresses' },
];

export default function AddEditUserModal({ user, onClose, onSave }) {
  const tabs = USER_EDIT_TABS;
  const [activeTab, setActiveTab] = useState('settings');
  const [formData, setFormData] = useState({
    user_type: user.user_type,
    is_admin: !!user.is_admin,
    monthly_invoicing_enabled: !!user.monthly_invoicing_enabled,
    credit_limit: user.credit_limit != null ? parseFloat(user.credit_limit) : 0,
    discount_threshold: user.discount_threshold != null ? parseFloat(user.discount_threshold) : 300,
    discount_percentage: user.discount_percentage != null ? parseFloat(user.discount_percentage) : 5,
  });

  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [billingAddresses, setBillingAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [shippingForm, setShippingForm] = useState({ ...EMPTY_SHIPPING });
  const [billingForm, setBillingForm] = useState({ ...EMPTY_BILLING });
  const [savingShipping, setSavingShipping] = useState(false);
  const [savingBilling, setSavingBilling] = useState(false);
  const [addressError, setAddressError] = useState(null);

  const overlayRef = useRef(null);
  const modalRef = useRef(null);

  // Fetch addresses when switching to address tabs
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setAddressesLoading(true);
        setAddressError(null);
        if (activeTab === 'shipping') {
          const data = await api.getAdminUserAddresses(user.id);
          if (!cancelled) setShippingAddresses(data);
        } else if (activeTab === 'billing') {
          const data = await api.getAdminUserBillingAddresses(user.id);
          if (!cancelled) setBillingAddresses(data);
        }
      } catch (err) {
        if (!cancelled) setAddressError(err.message);
      } finally {
        if (!cancelled) setAddressesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [activeTab, user.id]);

  // Trap focus inside modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key !== 'Tab' || !modalRef.current) return;
    const focusable = modalRef.current.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }, [onClose]);

  // Close on backdrop click
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  // Settings handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(user.id, formData);
  };

  // Shipping address handlers
  const handleShippingChange = (e) => {
    const { name, value, type, checked } = e.target;
    setShippingForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleShippingAddressSelect = (addressData) => {
    setShippingForm(prev => ({ ...prev, ...addressData }));
  };

  const handleAddShipping = async (e) => {
    e.preventDefault();
    try {
      setSavingShipping(true);
      setAddressError(null);
      const created = await api.addAdminShippingAddress(user.id, shippingForm);
      setShippingAddresses(prev =>
        [created, ...prev].map(a => ({ ...a, is_default: a.id === created.id ? created.is_default : a.is_default }))
          .sort((a, b) => Number(b.is_default) - Number(a.is_default))
      );
      setShippingForm({ ...EMPTY_SHIPPING });
    } catch (err) {
      setAddressError(err.message);
    } finally {
      setSavingShipping(false);
    }
  };

  const handleSetShippingDefault = async (addr) => {
    try {
      setAddressError(null);
      const updated = await api.setAdminShippingDefault(user.id, addr.id);
      setShippingAddresses(prev =>
        prev.map(a => a.id === updated.id ? updated : { ...a, is_default: false })
          .sort((a, b) => Number(b.is_default) - Number(a.is_default))
      );
    } catch (err) {
      setAddressError(err.message);
    }
  };

  // Billing address handlers
  const handleBillingChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBillingForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleBillingAddressSelect = (addressData) => {
    setBillingForm(prev => ({ ...prev, ...addressData }));
  };

  const handleAddBilling = async (e) => {
    e.preventDefault();
    try {
      setSavingBilling(true);
      setAddressError(null);
      const created = await api.addAdminBillingAddress(user.id, billingForm);
      setBillingAddresses(prev =>
        [created, ...prev].map(a => ({ ...a, is_default: a.id === created.id ? created.is_default : a.is_default }))
          .sort((a, b) => Number(b.is_default) - Number(a.is_default))
      );
      setBillingForm({ ...EMPTY_BILLING });
    } catch (err) {
      setAddressError(err.message);
    } finally {
      setSavingBilling(false);
    }
  };

  const handleSetBillingDefault = async (addr) => {
    try {
      setAddressError(null);
      const updated = await api.setAdminBillingDefault(user.id, addr.id);
      setBillingAddresses(prev =>
        prev.map(a => a.id === updated.id ? updated : { ...a, is_default: false })
          .sort((a, b) => Number(b.is_default) - Number(a.is_default))
      );
    } catch (err) {
      setAddressError(err.message);
    }
  };

  return (
    <div
      className="modal-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label={`Edit user ${user.name}`}
    >
      <div className="modal-content user-edit-modal" ref={modalRef}>
        <div className="modal-header">
          <h3>Edit User: {user.name}</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close dialog">
            <IconX size={20} />
          </button>
        </div>

        <p className="modal-subtitle">{user.email}</p>

        {/* Tabs */}
        <div className="modal-tabs" role="tablist" aria-label="Edit user sections">
          {tabs.map(tab => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeTab === tab.key}
              aria-controls={`tab-panel-${tab.key}`}
              id={`tab-${tab.key}`}
              className={`modal-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div role="tabpanel" id="tab-panel-settings" aria-labelledby="tab-settings">
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="edit-user-type">User Type:</label>
                <select id="edit-user-type" name="user_type" value={formData.user_type} onChange={handleChange}>
                  <option value="retail">Retail</option>
                  <option value="business">Business</option>
                </select>
              </div>

              <Checkbox
                name="is_admin"
                label="Is Administrator?"
                checked={formData.is_admin}
                onChange={(e) => handleChange({ target: { name: 'is_admin', type: 'checkbox', checked: e.currentTarget.checked } })}
              />

              <hr />
              <h4 id="edit-user-billing-title">Billing Permissions</h4>

              <Checkbox
                name="monthly_invoicing_enabled"
                label="Enable Monthly Invoicing"
                checked={formData.monthly_invoicing_enabled}
                onChange={(e) => handleChange({ target: { name: 'monthly_invoicing_enabled', type: 'checkbox', checked: e.currentTarget.checked } })}
              />

              <div className="form-group">
                <label htmlFor="edit-user-credit-limit" style={{ opacity: formData.monthly_invoicing_enabled ? 1 : 0.5 }}>
                  Monthly Credit Limit (CHF):
                </label>
                <input
                  id="edit-user-credit-limit"
                  type="number"
                  step="0.01"
                  name="credit_limit"
                  value={formData.credit_limit}
                  onChange={handleChange}
                  disabled={!formData.monthly_invoicing_enabled}
                  aria-describedby={!formData.monthly_invoicing_enabled ? 'edit-user-billing-disabled-note' : undefined}
                />
                {!formData.monthly_invoicing_enabled && (
                  <span id="edit-user-billing-disabled-note" className="sr-only">Available only when monthly invoicing is enabled.</span>
                )}
              </div>

              <hr />
              <h4 id="edit-user-discount-title">Discount Configuration</h4>

              <div className="form-group">
                <label htmlFor="edit-user-discount-threshold" style={{ opacity: formData.monthly_invoicing_enabled ? 1 : 0.5 }}>
                  Discount Threshold (CHF):
                </label>
                <input
                  id="edit-user-discount-threshold"
                  type="number"
                  step="0.01"
                  name="discount_threshold"
                  value={formData.discount_threshold}
                  onChange={handleChange}
                  disabled={!formData.monthly_invoicing_enabled}
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-user-discount-percentage" style={{ opacity: formData.monthly_invoicing_enabled ? 1 : 0.5 }}>
                  Discount Percentage (%):
                </label>
                <input
                  id="edit-user-discount-percentage"
                  type="number"
                  step="0.01"
                  name="discount_percentage"
                  value={formData.discount_percentage}
                  onChange={handleChange}
                  disabled={!formData.monthly_invoicing_enabled}
                />
              </div>

              <div className="btns-container">
                <Button variant="subtle" color="gray" onClick={onClose}>Cancel</Button>
                <Button type="submit">Save changes</Button>
              </div>
            </form>
          </div>
        )}

        {/* Shipping Addresses Tab */}
        {activeTab === 'shipping' && (
          <div role="tabpanel" id="tab-panel-shipping" aria-labelledby="tab-shipping">
            {addressError && <p className="address-error" role="alert">{addressError}</p>}

            <h4 id="edit-user-add-shipping-title">Add Shipping Address</h4>
            <AddressForm form={shippingForm} onChange={handleShippingChange} onSubmit={handleAddShipping} saving={savingShipping} type="Shipping">
              <AddressSearch onSelectResult={handleShippingAddressSelect} showDeliveryDisclaimer={false} />
            </AddressForm>

            <hr />

            <h4 id="edit-user-existing-shipping-title">Existing Shipping Addresses</h4>
            <AddressList addresses={shippingAddresses} loading={addressesLoading} onSetDefault={handleSetShippingDefault} type="shipping" />
          </div>
        )}

        {/* Billing Addresses Tab */}
        {activeTab === 'billing' && (
          <div role="tabpanel" id="tab-panel-billing" aria-labelledby="tab-billing">
            {addressError && <p className="address-error" role="alert">{addressError}</p>}

            <h4 id="edit-user-add-billing-title">Add Billing Address</h4>
            <AddressForm
              form={billingForm}
              onChange={handleBillingChange}
              onSubmit={handleAddBilling}
              saving={savingBilling}
              type="Billing"
              extraFields={
                <>
                  <div className="form-group">
                    <label htmlFor="edit-user-billing-company" className="sr-only">Company Name (optional)</label>
                    <input id="edit-user-billing-company" name="company_name" placeholder="Company Name (optional)" value={billingForm.company_name} onChange={handleBillingChange} aria-label="Company Name (optional)" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-user-billing-vat" className="sr-only">VAT Number (optional)</label>
                    <input id="edit-user-billing-vat" name="vat_number" placeholder="VAT Number (optional)" value={billingForm.vat_number} onChange={handleBillingChange} aria-label="VAT Number (optional)" />
                  </div>
                </>
              }
            >
              <AddressSearch onSelectResult={handleBillingAddressSelect} showDeliveryDisclaimer={false} />
            </AddressForm>

            <hr />

            <h4 id="edit-user-existing-billing-title">Existing Billing Addresses</h4>
            <AddressList addresses={billingAddresses} loading={addressesLoading} onSetDefault={handleSetBillingDefault} type="billing" />
          </div>
        )}
      </div>
    </div>
  );
}
